import { BookLock, LockKeyhole, ShieldAlert, Unlock } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { LedgerPostingPeriodResult, LedgerPostingPeriodRow } from '@/api/ledger-types'
import { ErrorSurface, ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDateTime, formatShortDate } from '@/lib/datetime'

import {
  formatPeriodName,
  PERIOD_STATUS_LABEL,
  PERIOD_STATUS_MEANING,
  PERIOD_STATUS_TONE,
} from './constants'
import { LedgerDualControlNotice, LedgerDualControlOutcome } from './dual-control'
import { useClosePostingPeriod, useReopenPostingPeriod } from './mutations'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'
import { usePostingPeriods } from './queries'

/**
 * Accounting periods.
 *
 * A period is a calendar month of the ledger, created the first time a day inside it is opened. Its
 * state decides what may still post into that month, and the three states are materially different
 * rather than degrees of the same thing — so each row says what its state actually permits rather
 * than only naming it:
 *
 * - **Open** — anything posts.
 * - **Soft closed** — system postings and reversals still land; a manual journal is refused whatever
 *   permission the operator holds. This is the state a month sits in between the last day's close
 *   and the numbers being reported.
 * - **Sealed** — nothing posts at all. The month has been reported.
 *
 * Two commands, and they are not equally serious. Closing is `ledger.period.close`. Reopening a
 * closed period is `ledger.period.reopen` and is **countersigned**, because it puts a month whose
 * figures may already have gone to the Bank of Ghana back into play.
 *
 * Read-only at any width; the two commands are dialogs rather than a screen, because neither keys an
 * amount — a period is chosen from a list and the only free input is a reason.
 */
export function PostingPeriodsSection() {
  const permissions = useLedgerPermissions()
  const [closing, setClosing] = useState<LedgerPostingPeriodRow | null>(null)
  const [reopening, setReopening] = useState<LedgerPostingPeriodRow | null>(null)

  const canRead = permissions.canClosePeriod || permissions.canReopenPeriod || permissions.canViewJournals
  const query = usePostingPeriods({ take: 36 }, canRead)

  if (!canRead) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
        <ShieldAlert className="size-6 text-warning-text" aria-hidden />
        <p className="text-sm font-medium text-heading">Period control is restricted</p>
        <p className="max-w-lg text-xs text-muted">
          Reading the accounting periods needs a ledger permission — closing one needs{' '}
          <span className="font-mono">{LEDGER_PERMISSIONS.periodClose}</span> and reopening one needs{' '}
          <span className="font-mono">{LEDGER_PERMISSIONS.periodReopen}</span>.
        </p>
      </div>
    )
  }

  if (query.isPending) {
    return <Skeleton className="h-96 w-full" />
  }

  if (query.isError) {
    return (
      <ErrorSurface
        error={query.error}
        onRetry={() => void query.refetch()}
        title="The accounting periods could not be read"
      />
    )
  }

  return (
    <div className="space-y-4">
      <Alert tone="info">
        <BookLock aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Closing a period is not the same as closing a day</AlertTitle>
          <AlertDescription>
            A day close settles one branch&apos;s postings. A period close settles a whole calendar
            month across the institution, and a soft close still lets the system post accruals and
            reversals into it. Sealing is the step taken once the month&apos;s numbers have actually
            been reported.
          </AlertDescription>
        </div>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Periods</CardTitle>
          <Badge tone="neutral">{query.data.length} months</Badge>
        </CardHeader>

        <CardContent className="p-0">
          {query.data.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-body">
              No accounting period exists yet. One is created the first time a business day inside a
              month is opened — so this list fills as the institution starts trading.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <TableHead scope="col" className="w-40">
                      Period
                    </TableHead>
                    <TableHead scope="col" className="w-32">
                      Status
                    </TableHead>
                    <TableHead scope="col">What it permits</TableHead>
                    <TableHead scope="col" className="w-44">
                      Closed
                    </TableHead>
                    <TableHead scope="col" className="w-56">
                      Action
                    </TableHead>
                  </tr>
                </TableHeader>
                <TableBody>
                  {query.data.map((period) => (
                    <TableRow key={`${String(period.year)}-${String(period.month)}`}>
                      <TableCell>
                        <span className="text-sm font-medium text-heading">
                          {formatPeriodName(period.year, period.month)}
                        </span>
                        <p className="numeric text-2xs tabular-nums text-muted">
                          {formatShortDate(period.startDate)} – {formatShortDate(period.endDate)}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge tone={PERIOD_STATUS_TONE[period.status]}>
                          {PERIOD_STATUS_LABEL[period.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-body">
                        {PERIOD_STATUS_MEANING[period.status]}
                      </TableCell>
                      <TableCell className="text-2xs text-muted">
                        <ClosedFacts period={period} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {period.status !== 'HardClosed' && permissions.canClosePeriod ? (
                            <Button variant="secondary" size="sm" onClick={() => setClosing(period)}>
                              <LockKeyhole />
                              {period.status === 'Open' ? 'Close' : 'Seal'}
                            </Button>
                          ) : null}
                          {period.status !== 'Open' && permissions.canReopenPeriod ? (
                            <Button variant="ghost" size="sm" onClick={() => setReopening(period)}>
                              <Unlock />
                              Reopen
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {closing ? (
        <ClosePeriodDialog period={closing} onClose={() => setClosing(null)} />
      ) : null}
      {reopening ? (
        <ReopenPeriodDialog period={reopening} onClose={() => setReopening(null)} />
      ) : null}
    </div>
  )
}

function ClosedFacts({ period }: { period: LedgerPostingPeriodRow }) {
  if (period.softClosedAtUtc === null && period.hardClosedAtUtc === null) {
    return <span>—</span>
  }

  return (
    <>
      {period.softClosedAtUtc ? (
        <p className="numeric tabular-nums">Soft {formatDateTime(period.softClosedAtUtc)}</p>
      ) : null}
      {period.hardClosedAtUtc ? (
        <p className="numeric tabular-nums">Sealed {formatDateTime(period.hardClosedAtUtc)}</p>
      ) : null}
      {/*
        The identifier, because that is what the row carries. No module serves a directory a ledger
        role can read, so a name here would have to be invented.
      */}
      {period.closedByUserId ? <p className="font-mono">user {period.closedByUserId}</p> : null}
    </>
  )
}

/**
 * Closing a period — soft, or sealed.
 *
 * The dialog makes the operator choose between them rather than defaulting, because the two are a
 * materially different act and the endpoint distinguishes them with a single boolean that is easy
 * to slide past.
 */
function ClosePeriodDialog({
  period,
  onClose,
}: {
  period: LedgerPostingPeriodRow
  onClose: () => void
}) {
  const [hard, setHard] = useState(period.status === 'SoftClosed')
  const [result, setResult] = useState<LedgerPostingPeriodResult | null>(null)

  const close = useClosePostingPeriod()
  const name = formatPeriodName(period.year, period.month)

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          close.reset()
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close {name}</DialogTitle>
          <DialogDescription>
            {formatShortDate(period.startDate)} to {formatShortDate(period.endDate)}, across the whole
            institution.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {result === null ? (
            <>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-heading">How far to close it</legend>

                <label className="flex cursor-pointer gap-3 rounded-control border border-border-subtle bg-surface p-3 max-lg:min-h-11">
                  <input
                    type="radio"
                    name="close-depth"
                    checked={!hard}
                    onChange={() => setHard(false)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-heading">Soft close</span>
                    <span className="block text-xs text-body">
                      {PERIOD_STATUS_MEANING.SoftClosed}
                    </span>
                  </span>
                </label>

                <label className="flex cursor-pointer gap-3 rounded-control border border-border-subtle bg-surface p-3 max-lg:min-h-11">
                  <input
                    type="radio"
                    name="close-depth"
                    checked={hard}
                    onChange={() => setHard(true)}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-heading">Seal it</span>
                    <span className="block text-xs text-body">{PERIOD_STATUS_MEANING.HardClosed}</span>
                  </span>
                </label>
              </fieldset>

              {hard ? (
                <Alert tone="warning">
                  <LockKeyhole aria-hidden />
                  <div className="space-y-1">
                    <AlertTitle>Seal it once the numbers are out, not before</AlertTitle>
                    <AlertDescription>
                      Nothing posts into a sealed month — not an accrual, not a reversal of an error
                      found next week. Undoing it means a countersigned reopening.
                    </AlertDescription>
                  </div>
                </Alert>
              ) : null}

              {close.isError ? <ProblemAlert error={close.error} /> : null}
            </>
          ) : (
            <LedgerDualControlOutcome
              result={result}
              awaitingDescription={
                <>
                  The close of {name} is stored and waiting for a countersignature.{' '}
                  <strong>The period is unchanged</strong> — it is still{' '}
                  {PERIOD_STATUS_LABEL[result.period.status].toLowerCase()}.
                </>
              }
              appliedDescription={
                <>
                  {name} is now {PERIOD_STATUS_LABEL[result.period.status].toLowerCase()}.{' '}
                  {PERIOD_STATUS_MEANING[result.period.status]}
                </>
              }
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              close.reset()
              onClose()
            }}
            disabled={close.isPending}
          >
            {result === null ? 'Cancel' : 'Close'}
          </Button>
          {result === null ? (
            <Button
              variant={hard ? 'danger' : 'primary'}
              disabled={close.isPending}
              onClick={() => {
                close.mutate(
                  { year: period.year, month: period.month, hard },
                  { onSuccess: setResult },
                )
              }}
            >
              {close.isPending ? 'Working…' : hard ? 'Seal the period' : 'Soft close the period'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Reopening a closed period. **Countersigned.**
 *
 * The reason is mandatory and travels to the checker, who is being asked to agree that a month whose
 * figures may already have been reported should be movable again. The outcome reads
 * `awaitingApproval` rather than the period's status: on a queued request the period comes back
 * unchanged and still closed, and a screen reading the status alone would report the reopening as
 * having quietly failed.
 */
function ReopenPeriodDialog({
  period,
  onClose,
}: {
  period: LedgerPostingPeriodRow
  onClose: () => void
}) {
  const permissions = useLedgerPermissions()
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<LedgerPostingPeriodResult | null>(null)

  const reopen = useReopenPostingPeriod()
  const name = formatPeriodName(period.year, period.month)

  // A dialog that is unmounted between uses still resets explicitly, so that a future refactor to a
  // long-lived dialog cannot silently carry one period's reason onto another.
  useEffect(() => {
    setReason('')
    setResult(null)
  }, [period.year, period.month])

  const trimmed = reason.trim()

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          reopen.reset()
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reopen {name}</DialogTitle>
          <DialogDescription>
            Currently {PERIOD_STATUS_LABEL[period.status].toLowerCase()}. Reopening puts the whole
            month back into play across the institution.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {result === null ? (
            <>
              <LedgerDualControlNotice
                action="Reopening a closed period"
                approvePermission={LEDGER_PERMISSIONS.periodReopenApprove}
                canCountersign={permissions.canCountersignReopen}
              />

              {period.status === 'HardClosed' ? (
                <Alert tone="warning">
                  <BookLock aria-hidden />
                  <div className="space-y-1">
                    <AlertTitle>This month was sealed</AlertTitle>
                    <AlertDescription>
                      A sealed period is one whose numbers have been reported. Reopening it means the
                      figures already filed may stop agreeing with the ledger, and somebody has to
                      account for the difference.
                    </AlertDescription>
                  </div>
                </Alert>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="reopen-reason">Why does this month need to move again?</Label>
                <textarea
                  id="reopen-reason"
                  rows={3}
                  autoFocus
                  value={reason}
                  placeholder="What has to be posted, and why it cannot go into the current period."
                  onChange={(event) => setReason(event.target.value)}
                  aria-describedby="reopen-reason-hint"
                  className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading outline-none transition-colors placeholder:text-muted focus:border-brand-text hover:border-border-strong"
                />
                <p id="reopen-reason-hint" className="text-xs text-muted">
                  Mandatory. It is what the countersigner reads before agreeing, and what an auditor
                  reads when the month is looked at again.
                </p>
              </div>

              {reopen.isError ? <ProblemAlert error={reopen.error} /> : null}
            </>
          ) : (
            <LedgerDualControlOutcome
              result={result}
              awaitingDescription={
                <>
                  The reopening of {name} is stored and waiting for a countersignature.{' '}
                  <strong>The period is still {PERIOD_STATUS_LABEL[result.period.status].toLowerCase()}</strong>{' '}
                  and nothing may post into it yet.
                </>
              }
              appliedDescription={
                <>
                  {name} is open again. {PERIOD_STATUS_MEANING[result.period.status]}
                </>
              }
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              reopen.reset()
              onClose()
            }}
            disabled={reopen.isPending}
          >
            {result === null ? 'Cancel' : 'Close'}
          </Button>
          {result === null ? (
            <Button
              variant="danger"
              disabled={trimmed === '' || reopen.isPending}
              onClick={() => {
                reopen.mutate(
                  { year: period.year, month: period.month, reason: trimmed },
                  { onSuccess: setResult },
                )
              }}
            >
              {reopen.isPending ? 'Working…' : 'Reopen the period'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
